import type { Row } from "@tanstack/vue-table";
import type WinBox from "winbox";
import { z } from "zod";

export const DataTypesEnum = z.enum([
	"Profile",
	"Text",
	"SampleText",
	"Feature",
	"CorpusText",
	"BiblioEntries",
]);
export type DataTypesEnum = z.infer<typeof DataTypesEnum>;

export interface DataType {
	name: string;
	targetType: DataTypesEnum;
	categoryId: string;
	collection: string;
	explore_xslt?: string;
	contentTypeHeading: string;
}
export type DataTypes = Record<DataTypesEnum, DataType>;

interface WindowItemBase {
	id: string;
	winbox?: WinBox;
	label: string;
}

export const TextId = z.object({
	textId: z.string(),
});
export const TeiSource = z.object({
	teiSource: z.string(),
});
export const ShowCitation = z.object({
	showCitation: z.boolean(),
});
export const QueryString = z.object({
	queryString: z.string(),
});

export const ExploreSamplesQueryBase = z.object({
	ids: z.string().optional(),
	word: z.string().optional(),
	person: z.string().optional(),
	translation: z.string().optional(),
	comment: z.string().optional(),
	features: z.string().optional(),
	page: z.number().optional(),
});

export const ExploreSamplesQueryParams = ExploreSamplesQueryBase.extend({
	dataType: z.enum(["SampleText", "Feature"]),
	region: z.string().optional(),
	settlement: z.string().optional(),
}).extend(ShowCitation.partial().shape);

export const ExploreSamplesQueryDbParams = ExploreSamplesQueryBase.extend({
	type: z.enum(["samples", "lingfeatures"]),
});

export const ExploreSamplesFormParams = z.object({
	dataTypes: z.array(DataTypesEnum),
});

export const ExploreSamplesSchema = z.object({
	targetType: z.literal("ExploreSamples"),
	params: ExploreSamplesQueryParams.extend(TextId.partial().shape).extend(
		ShowCitation.partial().shape,
	),
});

export type ExploreSamplesWindowItem = WindowItemBase & z.infer<typeof ExploreSamplesSchema>;

export const ExploreSamplesFormSchema = z.object({
	targetType: z.literal("ExploreSamplesForm"),
	params: z
		.object({
			dataTypes: z.array(DataTypesEnum),
		})
		.extend(TextId.partial().shape)
		.extend(ShowCitation.partial().shape),
});

export type ExploreSamplesFormWindowItem = WindowItemBase &
	z.infer<typeof ExploreSamplesFormSchema>;

export const BibliographyEntriesSchema = z.object({
	targetType: z.literal("BiblioEntries"),
	params: QueryString.extend({
		xslt: z.string().optional(),
		showMap: z.boolean().optional(),
		isQueryVisible: z.boolean().optional(),
	}).extend(ShowCitation.partial().shape),
});
export type BibliographyEntriesWindowItem = WindowItemBase &
	z.infer<typeof BibliographyEntriesSchema>;

export const Dict = z.object({
	id: z.string(),
	queryTemplates: z.map(z.string(), z.string()),
	dbNames: z.array(z.string()),
	specialCharacters: z.array(z.object({ value: z.string(), text: z.string().optional() })),
});
export const DictQuerySchema = z.object({
	targetType: z.literal("DictQuery"),
	params: z
		.object({
			textId: Dict.shape.id,
			queryParams: z
				.object({
					q: z.string().optional().nullable(),
					page: z.number().optional().nullable(),
					pageSize: z.number().optional().nullable(),
					id: z.string().optional().nullable(),
					ids: z.string().optional().nullable(),
					sort: z.enum(["asc", "desc", "none"]).optional().nullable(),
					altLemma: z.string().optional().nullable(),
					format: z.string().optional().nullable(),
				})
				.optional(),
			queryString: z.string(),
			queryTemplateTextInput: z.string().optional(),
			queryTemplate: z.string().optional(),
			isTextInputManual: z.boolean().optional().default(false),
			isQueryVisible: z.boolean().optional().default(true),
		})
		.extend(ShowCitation.partial().shape),
});
export type DictQueryWindowItem = WindowItemBase & z.infer<typeof DictQuerySchema>;

export const CorpusQuerySchema = z.object({
	targetType: z.literal("CorpusQuery"),
	params: QueryString.extend({
		mode: z.enum(["tag", "text"]).optional(),
	}).extend(ShowCitation.partial().shape),
});
export type CorpusQueryWindowItem = WindowItemBase & z.infer<typeof CorpusQuerySchema>;

export const CorpusTextSchema = z.object({
	targetType: z.literal("CorpusText"),
	params: TextId.extend({
		hits: z.string().optional(),
		u: z.string().optional(), // TODO: give this parameter a telling name
	})
		.extend(ShowCitation.partial().shape)
		.extend(TeiSource.partial().shape),
});
export type CorpusTextWindowItem = WindowItemBase & z.infer<typeof CorpusTextSchema>;

export const FeatureSchema = z.object({
	targetType: z.literal("Feature"),
	params: TextId.extend(TeiSource.partial().shape).extend(ShowCitation.partial().shape),
});
export type FeatureWindowItem = WindowItemBase & z.infer<typeof FeatureSchema>;

export const FeatureStatisticsSchema = z.object({
	targetType: z.literal("FeatureStatistics"),
	params: z.object({ featureId: z.string() }).extend(ShowCitation.partial().shape),
});
export type FeatureStatisticsWindowItem = WindowItemBase & z.infer<typeof FeatureStatisticsSchema>;

export const FeatureValueSchema = z.object({
	targetType: z.literal("FeatureValue"),
	params: z.object({ values: z.array(z.any()) }).extend(ShowCitation.partial().shape),
});
export type FeatureValueWindowItem = WindowItemBase & z.infer<typeof FeatureValueSchema>;

export const GeoFeatureSchema = z.object({
	type: z.literal("Feature"),
	id: z.string(),
	geometry: z.object({
		type: z.literal("Point"),
		coordinates: z.array(z.number()),
	}),
	properties: z.any(),
});
export type FeatureType = z.infer<typeof GeoFeatureSchema>;

export const FeatureCollectionSchema = z.object({
	type: z.literal("FeatureCollection"),
	properties: z.object({
		name: z.string(),
		column_headings: z.array(z.any()),
	}),
	features: z.array(GeoFeatureSchema),
});
export type FeatureCollectionType = z.infer<typeof FeatureCollectionSchema>;

export const LocationSchema = z.object({
	targetType: z.literal("Location"),
	params: z.object<Row<FeatureType>>().extend(ShowCitation.partial().shape).loose(),
});
export type LocationWindowItem = WindowItemBase & z.infer<typeof LocationSchema>;

export const CompareMarkersParams = z.object();

export const GeoMapScope = z.enum(["reg", "geo", "diaGroup"]);
export const DataListMapMarkerSchema = z.object({
	id: z.string(),
	label: z.string(),
	placeName: z.string().optional(),
	alt: z.string().optional(),
	dataType: DataTypesEnum,
	coordinates: z.tuple([z.number(), z.number()]),
});
export const DataListMapLayerSchema = z.object({
	id: z.string(),
	title: z.string(),
	color: z.string(),
	enabled: z.boolean().optional(),
	markers: z.array(DataListMapMarkerSchema),
});
export type DataListMapLayer = z.infer<typeof DataListMapLayerSchema>;
export const GeoMapSchema = z.object({
	targetType: z.literal("WMap"),
	params: QueryString.extend({
		title: z.string().optional(),
		endpoint: z.string(),
		queryParams: ExploreSamplesQueryDbParams.optional(),
		scope: z.array(GeoMapScope).optional(),
		hideDefaultLayers: z.boolean().optional(),
		dataListLayers: z.array(DataListMapLayerSchema).optional(),
	}).extend(ShowCitation.partial().shape),
});
export type GeoMapWindowItem = WindowItemBase & z.infer<typeof GeoMapSchema>;
export const GeoMapSubnavItemSchema = z.intersection(
	GeoMapSchema,
	z.object({ id: z.string(), title: z.string() }),
);

export const ProfileSchema = z.object({
	targetType: z.literal("Profile"),
	params: TextId.extend(TeiSource.partial().shape).extend(ShowCitation.partial().shape),
});
export type ProfileWindowItem = WindowItemBase & z.infer<typeof ProfileSchema>;

export const TextSchema = z.object({
	targetType: z.literal("Text"),
	params: TextId.extend(TeiSource.partial().shape).extend(ShowCitation.partial().shape),
});
export type TextWindowItem = WindowItemBase & z.infer<typeof TextSchema>;

export const SampleTextSchema = z.object({
	targetType: z.literal("SampleText"),
	params: TextId.extend(TeiSource.partial().shape).extend(ShowCitation.partial().shape),
});
export type SampleTextWindowItem = WindowItemBase & z.infer<typeof SampleTextSchema>;

export const FeatureValueGroupMember = z.object({
	columnId: z.string(),
	value: z.string(),
});
export type FeatureValueGroupMember = z.infer<typeof FeatureValueGroupMember>;

const LegacyFeatureValueGroup = z
	.object({
		columnId: z.string(),
		label: z.string(),
		values: z.array(z.string()),
	})
	.transform(({ columnId, label, values }) => ({
		label,
		values: values.map((value) => ({ columnId, value })),
	}));

const FeatureValueGroupObject = z.object({
	label: z.string(),
	values: z.array(FeatureValueGroupMember),
});
export const FeatureValueGroup = z.union([FeatureValueGroupObject, LegacyFeatureValueGroup]);
export type FeatureValueGroup = z.infer<typeof FeatureValueGroupObject>;

export const ListMapSchema = z.object({
	targetType: z.literal("ListMap"),
	params: z
		.object({
			queryString: z.string().default(""),
			featureValueGroups: z.array(FeatureValueGroup).optional(),
		})
		.extend(ShowCitation.partial().shape),
});
export type ListMapWindowItem = WindowItemBase & z.infer<typeof ListMapSchema>;

export const MarkerEnum = z.enum(["petal", "default"]);
export type MarkerType = z.infer<typeof MarkerEnum>;
export const GeojsonMapSchema = z.object({
	targetType: z.literal("GeojsonMap"),
	params: z
		.object({
			markerType: MarkerEnum.optional(),
		})
		.extend(ShowCitation.partial().shape),
});
export type GeojsonMapWindowItem = WindowItemBase & z.infer<typeof GeojsonMapSchema>;

export const SimpleMetadataListSortMode = z.enum(["hit-count", "alphabetical"]);
export const SimpleMetadataListState = z.object({
	sortMode: SimpleMetadataListSortMode.optional(),
	globalFilter: z.string().optional(),
	facets: z.record(z.string(), z.array(z.string())).optional(),
});
export type SimpleMetadataListState = z.infer<typeof SimpleMetadataListState>;

export const DataListSchema = z.object({
	targetType: z.literal("DataList"),
	params: z
		.object({
			dataTypes: z.array(DataTypesEnum),
			filterListBy: z
				.object({
					key: z.string(),
					value: z.string(),
				})
				.optional(),
			listState: SimpleMetadataListState.optional(),
			mapEnabled: z.boolean().optional(),
			mapSyncId: z.string().optional(),
		})
		.extend(TextId.partial().shape)
		.extend(ShowCitation.partial().shape),
});

export type DataListWindowItem = WindowItemBase & z.infer<typeof DataListSchema>;

export const FeatureDescriptionListSchema = z.object({
	targetType: z.literal("FeatureDescriptionList"),
	params: z
		.object({
			/** Dot separated category path the list is scoped to, e.g. `personal_pronouns`. */
			taxonomyPath: z.string().optional(),
			globalFilter: z.string().optional(),
		})
		.extend(TextId.partial().shape)
		.extend(ShowCitation.partial().shape),
});

export type FeatureDescriptionListWindowItem = WindowItemBase &
	z.infer<typeof FeatureDescriptionListSchema>;

export const DataTableSchema = z.object({
	targetType: z.literal("DataTable"),
	params: z
		.object({
			dataTypes: z.array(DataTypesEnum),
			filters: z
				.array(
					z.object({
						key: z.string(),
						value: z.string(),
					}),
				)
				.optional(),
		})
		.extend(TextId.partial().shape)
		.extend(TeiSource.partial().shape)
		.extend(ShowCitation.partial().shape),
});
export type DataTableWindowItem = WindowItemBase & z.infer<typeof DataTableSchema>;

export const Schema = z.discriminatedUnion("targetType", [
	BibliographyEntriesSchema,
	DictQuerySchema,
	CorpusQuerySchema,
	CorpusTextSchema,
	FeatureSchema,
	FeatureStatisticsSchema,
	FeatureValueSchema,
	GeoMapSchema,
	ProfileSchema,
	TextSchema,
	SampleTextSchema,
	ListMapSchema,
	GeojsonMapSchema,
	DataListSchema,
	FeatureDescriptionListSchema,
	DataTableSchema,
	ExploreSamplesSchema,
	ExploreSamplesFormSchema,
	LocationSchema,
]);
export type WindowItem = WindowItemBase & z.infer<typeof Schema>;
export type OpenWindowItem = WindowItem & { winbox: NonNullable<WindowItem["winbox"]> };

export type WindowItemTargetType = WindowItem["targetType"];

export type WindowItemMap = {
	[TargetType in WindowItemTargetType]: Extract<WindowItem, { targetType: TargetType }>;
};

export interface VicavHTTPError extends Error {
	status?: number;
	error?: Record<string, string>;
}
